import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { googleLogin, googleRegister } from "../backend/api/auth";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { useUserStore } from "../stores/UserStore";
import { useLoginAndRegisterStore } from "../stores/LoginAndRegisterStore";
import { AxiosError } from "axios";
import googleIcon from "../assets/google.svg";
import { trackRegistration, trackLogin } from "../utils/analytics";
interface LoginRegisterWithGoogleProps {
  type: "login" | "register";
}

const LoginRegisterWithGoogle: React.FC<LoginRegisterWithGoogleProps> = ({
  type,
}) => {
  const { t, i18n } = useTranslation();
  const [token, setToken] = useState("");
  const { setUser } = useUserStore();
  const { close } = useLoginAndRegisterStore();

  //REGISTRAZIONE
  const googleRegisterMutation = useMutation({
    mutationFn: (googleToken: string) =>
      googleRegister({ googleToken }, i18n.language),
    onSuccess: (data) => {
      console.log(data);
      toast.success(t("registerWithGoogleSuccess"));
      setUser({ ...data.user, access_token: data.access_token });

      // Track registration in Google Analytics
      trackRegistration("google", data.user.id);

      close();
    },
    onError: (error: AxiosError) => {
      if (error.status === 409) {
        toast.success(t("tryLogin"), { duration: 8000 });
      }
    },
  });
  //LOGIN
  const googleLoginMutation = useMutation({
    mutationFn: (googleToken: string) =>
      googleLogin({ googleToken }, i18n.language),
    onSuccess: (data) => {
      console.log(data);
      toast.success(t("loginWithGoogleSuccess"));
      setUser({ ...data.user, access_token: data.access_token });

      // Track login in Google Analytics
      trackLogin("google", data.user.id);

      close();
    },
    onError: (error: AxiosError) => {
      if (error.status === 409) {
        toast.error(t("tryLoginWithEmail"), { duration: 8000 });
        return;
      }
      if (error.status === 401) {
        toast.error(t("tryRegisterWithGoogle"), { duration: 8000 });
        return;
      }
      toast.error(t("loginWithGoogleError"));
    },
  });

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_APP_GOOGLE_CLIENT_ID!}>
      <div className="flex items-center justify-center w-full relative mt-2">
        <div
          className=" w-full h-auto"
          id="googleLoginCustom"
          onClick={() =>
            (
              document
                .getElementById("googleLoginCustom")
                ?.querySelector('[role="button"]') as HTMLButtonElement
            )?.click()
          }
        >
          <GoogleLogin
            useOneTap={false}
            auto_select={false}
            shape="pill"
            size="large"
            onSuccess={({ credential }) =>
              credential && type === "login"
                ? googleLoginMutation.mutate(credential)
                : googleRegisterMutation.mutate(credential)
            }
            onError={() => toast.error(t("googleError"))}
            // Queste prop riducono la probabilità che Google provi a personalizzare il tasto
            type="standard"
            theme="outline"
            width="100%" // Copre tutta l'area del tuo tasto sotto
          />
        </div>
        <button
          disabled={googleLoginMutation.isPending}
          className={`
    w-full flex items-center justify-center  gap-2
    bg-white border-2 border-gray-300 rounded-md
    shadow-sm h-[36px] min-h-[36px] px-4
    font-medium text-gray-800 text-base
    transition-all duration-200
    hover:border-primary hover:shadow-lg hover:scale-[1.02]
    focus:outline-none focus:ring-2 focus:ring-primary/30
    disabled:opacity-60 disabled:cursor-not-allowed hidden
  `}
        >
          {googleLoginMutation.isPending ? (
            <span className="ml-2">
              {t("loginWithGoogle") || "Login con Google"}...
            </span>
          ) : (
            <>
              <img
                src={googleIcon}
                alt="Google"
                width={16}
                height={16}
                className="mr-2"
                style={{ display: "inline-block" }}
              />
              <span className="text-[14px]">
                {type === "login"
                  ? t("loginWithGoogle")
                  : t("registerWithGoogle")}
              </span>
            </>
          )}
        </button>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginRegisterWithGoogle;
